import React from "react";
import { useTranslation } from "react-i18next";

import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { UserRound } from "lucide-react";
import { format } from "date-fns";

import i18n from "@/i18n";
import BackButton from "@/components/back-button";
import MainGrid from "@/components/main-grid";
import StarRating from "@/components/star-rating";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { localeFor } from "@/utils/formatters";

import { ShiftDetails } from "@/routes/app/$role/_app/search/-components/shift-details";
import { Logger } from "@/utils/logger";

import { makeProfileQuery } from "./-queries";
import Button from "@/components/button";
import { useRole } from "@/utils/hooks/use-role";

import { ShiftCardItem } from "@/components/shift-card";
import { shiftToCard } from "@/utils/shift-card";
import { searchShifts } from "@/queries/api/searchApi";
import type { BusinessProfileResponse } from "@/schemas";

export const Route = createFileRoute("/app/profile/employer/$employerId/")({
  component: BusinessProfilePage,
});

function Chip({ title, text }: { title: string; text: string }) {
  return (
    <div className="w-20 h-20 rounded-sm p-2 bg-fog text-garnet flex flex-col gap-1 items-center">
      <p className="body-xs text-center">{title}</p>
      <p className="heading-2">{text}</p>
    </div>
  );
}

function Profile({
  business,
  metrics,
}: {
  business: any;
  metrics: BusinessProfileResponse["metrics"];
}) {
  const { t } = useTranslation("profile");
  const navigate = useNavigate();
  const role = useRole();

  return (
    <div className="w-full rounded-t-3xl bg-white min-h-[1024px]">
      <div className="w-full flex flex-col items-center">
        <img
          src={business?.banner_image_url || "/assets/2025-09-04-23-36-14.png"}
          className="object-cover h-[124px] rounded-t-3xl aspect-[411/124]"
        />
        <Avatar className="-top-[20px] h-[65px] w-[65px]">
          <AvatarImage src={business?.image_url} />
          <AvatarFallback>
            <UserRound />
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="w-full flex flex-col items-center gap-5 px-5">
        <div className="flex flex-col items-center">
          <h2 className="heading-2">{business?.name ?? "—"}</h2>
          <p className="body-xs text-slate">
            {t("employer.activeSince", {
              date: business?.createdAt
                ? format(new Date(business.createdAt), "MMMM, yyyy")
                : "—",
            })}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-[20px]">
          <Chip
            title={t("employer.responseTime")}
            text={
              metrics
                ? `${(metrics.response_time_seconds / 3600).toFixed(1)}h`
                : "—"
            }
          />
          <Chip
            title={t("employer.shiftsActive")}
            text={metrics ? String(metrics.active_shifts) : "—"}
          />
          <Chip
            title={t("employer.shiftsCompleted")}
            text={metrics ? String(metrics.shift_completed) : "—"}
          />
          <div className="w-20 h-20 rounded-sm p-2 bg-fog text-garnet flex flex-col gap-1 items-center justify-center">
            <p className="body-xs text-center">{t("employer.ratingChip")}</p>
            <StarRating
              value={business?.overall_rating}
              count={business?.rating_count}
              size="sm"
            />
          </div>
        </div>
        <p className="body-small">
          {business?.description ?? t("employer.noDescription")}
        </p>
        <div className="w-full h-[1px] bg-[#545A6340] mt-5" />
        <Button
          variant="secondary"
          onClick={() =>
            navigate({
              to: "/app/$role/messages",
              params: { role },
              search: {
                tab: "messages",
                to: business.user_id,
                businessId: business.id,
                selectedOfferId: undefined,
              },
            })
          }
        >
          {t("employer.sendMessage")}
        </Button>
      </div>
    </div>
  );
}

function CurrentShifts({ businessId }: { businessId?: string }) {
  const { t } = useTranslation("profile");
  const { data } = useQuery({
    queryKey: ["shifts", businessId, "PUBLISHED"],
    queryFn: () =>
      searchShifts({
        business_id: businessId,
        page_size: 20,
        page: 0,
        status: "PUBLISHED",
      }),
    enabled: !!businessId,
  });
  const shifts = data?.shifts ?? [];
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const cards = shifts.map((s) => shiftToCard(s));
  const selectedShift = selectedId
    ? shifts.find((s) => String(s.id) === selectedId)
    : (null as any);
  return (
    <div className="col-span-2 w-full rounded-t-3xl bg-white min-h-[1024px] p-6">
      <h2 className="heading-2">{t("employer.currentShifts")}</h2>
      <div className="w-full h-[1px] bg-[#545A6340] my-5" />
      <div className="grid grid-cols-2 space-x-10 space-y-[10px]">
        {cards.map((card) => (
          <ShiftCardItem
            key={card.id}
            s={card}
            onClick={() => setSelectedId(card.id)}
            hideUser
            hideLocation
            showVenue
          />
        ))}
      </div>
      {selectedShift && (
        <ShiftDetails
          shift={{
            id: String(selectedShift.id),
            title: selectedShift.role_name,
            venue:
              selectedShift.address?.address1 ?? selectedShift.location ?? "",
            lat: selectedShift.latitude,
            lng: selectedShift.longitude,
            date:
              selectedShift.start_date && selectedShift.end_date
                ? `${new Date(selectedShift.start_date).toLocaleDateString(localeFor(i18n.language || "en"), { month: "short", day: "numeric", year: "numeric" })} – ${new Date(selectedShift.end_date).toLocaleDateString(localeFor(i18n.language || "en"), { month: "short", day: "numeric", year: "numeric" })}`
                : selectedShift.start_date
                  ? new Date(selectedShift.start_date).toLocaleDateString(
                      localeFor(i18n.language || "en"),
                      { month: "short", day: "numeric", year: "numeric" },
                    )
                  : "",
            time:
              selectedShift.start_time && selectedShift.end_time
                ? `${selectedShift.start_time.slice(0, 5)} – ${selectedShift.end_time.slice(0, 5)}`
                : "",
            rate: selectedShift.wage,
            total: undefined,
            notes: selectedShift.role_description,
            instant: undefined,
            offer: undefined,
            allow_instant_acceptance: selectedShift.allow_instant_acceptance,
            require_training: selectedShift.require_training,
            business: { id: selectedShift.business?.id },
          }}
          role="employer"
          onClose={() => setSelectedId(null)}
          onInterested={(id) => {
            Logger.debug("Interested click", { id });
          }}
          onMessage={(id) => {
            Logger.debug("Message click", { id });
          }}
        />
      )}
    </div>
  );
}

function BusinessProfilePage() {
  const { employerId } = Route.useParams();
  const { data } = useSuspenseQuery(makeProfileQuery(employerId));

  const { business, metrics } = data;

  return (
    <div className="flex flex-col w-full gap-3 bg-fog">
      <div className="flex items-start">
        <BackButton />
      </div>

      <MainGrid>
        <Profile business={business} metrics={metrics} />
        <CurrentShifts businessId={business.id != null ? String(business.id) : undefined} />
      </MainGrid>
    </div>
  );
}
